import type { TFunction } from "i18next";
import {
  SCORE_CONFIGS,
  ScoreType,
  getScoreKey
} from "@/pages/deals/scoreConfig";

interface ScoreFilterOption {
  value: string;
  label: string;
}

// Score badges share a fixed severity ladder; render filter options in that
// order regardless of how SCORE_CONFIGS happens to be keyed.
const SCORE_KEY_ORDER = ["failed", "pending", "moderate", "pass", "fullpass"];

// A score's place on that ladder, so groups of a score column follow it too;
// a missing or unrecognised score has none.
export const scoreRank = (scoreText?: string | null): number | null => {
  if (!scoreText) return null;
  const rank = SCORE_KEY_ORDER.indexOf(getScoreKey(scoreText));
  return rank < 0 ? null : rank;
};

// The badge derives its score key by lowercasing the stored score_text, but the
// filter must send back the exact stored value. Map each score key to the
// canonical score_text the backend persists (dealsapi ScoreStatus StrEnum).
const SCORE_TEXT_BY_KEY: Record<string, string> = {
  failed: "Failed",
  pending: "Pending",
  moderate: "Moderate",
  pass: "Pass",
  fullpass: "FullPass"
};

// Build map-filter options for a questionnaire score from its badge value
// mapping, so the filter labels match the badges shown in the table (e.g.
// champion "pass" → "Strong"). The value is the stored score_text the deal
// filter matches against.
export const buildScoreFilterValues = (
  scoreType: ScoreType,
  translate: TFunction<"translation", undefined>
): ScoreFilterOption[] =>
  Object.entries(SCORE_CONFIGS[scoreType].valueMapping)
    .filter(([scoreKey, token]) => Boolean(token) && SCORE_TEXT_BY_KEY[scoreKey])
    .sort(
      ([firstKey], [secondKey]) =>
        SCORE_KEY_ORDER.indexOf(firstKey) - SCORE_KEY_ORDER.indexOf(secondKey)
    )
    .map(([scoreKey, token]) => ({
      value: SCORE_TEXT_BY_KEY[scoreKey],
      label: translate(`deals.kpis.${scoreType}Scores.${token}`)
    }));
